import React, { useEffect, useState } from 'react';
import { Box, Dialog, Typography, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import DosenButton from '../../../../components/ui/Buttons/DosenButton';
import { apiMessage } from '../../../../api/capability';
import { nudgeMember } from '../../../../api/memberNudge';

interface NudgeMemberDialogProps {
  open: boolean;
  memberId: string;
  memberName: string;
  /** Platform admins name the org; everyone else's token carries it. */
  orgId?: string;
  onClose: () => void;
}

const MAX_MESSAGE = 1000;

const DEFAULT_MESSAGE = 'Please complete outstanding profile fields.';

const NudgeMemberDialog = ({ open, memberId, memberName, orgId, onClose }: NudgeMemberDialogProps) => {
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setMessage(DEFAULT_MESSAGE);
      setFailure(null);
    }
  }, [open]);

  const handleClose = () => {
    if (sending) return;
    onClose();
  };

  // Delivered to the member's bell, and by email when system emails are on (POST /v3/admin/members/{id}/nudge).
  const handleSend = async () => {
    setSending(true);
    setFailure(null);
    try {
      const result = await nudgeMember(memberId, message.trim(), orgId);
      toast.success(
        result.email === 'sent'
          ? `Nudge sent to ${memberName}: in their notifications and by email.`
          : result.email === 'failed'
          ? `Nudge sent to ${memberName}'s notifications. The email could not be sent.`
          : `Nudge sent to ${memberName}'s notifications.`
      );
      onClose();
    } catch (err) {
      setFailure(apiMessage(err, 'The nudge could not be sent. Try again.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: { bgcolor: '#0F0F0F', border: '1px solid #2A2A2A', borderRadius: 2, width: 420, maxWidth: '90vw' }
      }}
    >
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#FDFDFD', fontFamily: 'Inter, sans-serif' }}>
            Nudge {memberName}
          </Typography>
          <CloseIcon onClick={handleClose} sx={{ color: '#868686', cursor: sending ? 'default' : 'pointer' }} />
        </Box>

        <TextField
          multiline
          minRows={3}
          fullWidth
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={sending}
          inputProps={{ 'aria-label': `Message to ${memberName}`, maxLength: MAX_MESSAGE }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: '#1C1C1C',
              color: '#FDFDFD',
              fontFamily: 'Inter, sans-serif',
              fontSize: 14,
              '& fieldset': { borderColor: '#2A2A2A' },
              '&:hover fieldset': { borderColor: '#565656' },
              '&.Mui-focused fieldset': { borderColor: '#F79420' }
            }
          }}
        />

        {failure && (
          <Typography role="alert" sx={{ mt: 1.5, fontSize: 13, color: '#FF6A55', fontFamily: 'Inter, sans-serif' }}>
            {failure}
          </Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <DosenButton
            tone="cta"
            sizeVariant="sm"
            onClick={handleSend}
            disabled={sending || !message.trim()}
            loading={sending}
            loadingText="Sending..."
          >
            Send
          </DosenButton>
        </Box>
      </Box>
    </Dialog>
  );
};

export default NudgeMemberDialog;
